import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Appearance = 'system' | 'light' | 'dark';
export type Accent = 'coral' | 'violet' | 'emerald' | 'cobalt';
// Cobalt leads: it is the fresh-account default (design system v0.5); stored choices are kept.
export const ACCENTS: Accent[] = ['cobalt', 'coral', 'violet', 'emerald'];
export const APPEARANCES: Appearance[] = ['system', 'light', 'dark'];

export interface ThemePrefs { appearance: Appearance; accent: Accent }
interface ThemeContextValue extends ThemePrefs {
  resolved: 'light' | 'dark';
  setAppearance(a: Appearance): void;
  setAccent(a: Accent): void;
  setPrefs(p: Partial<ThemePrefs>): void;
}

const STORAGE_KEY = 'dz-theme';
const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): ThemePrefs | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (APPEARANCES.includes(v.appearance) && ACCENTS.includes(v.accent)) return v;
  } catch { /* storage unavailable */ }
  return null;
}

function systemDark() {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Applies appearance + accent to <html>. The logo and mascot never take the accent colour. */
export function applyTheme(p: ThemePrefs) {
  const resolved = p.appearance === 'system' ? (systemDark() ? 'dark' : 'light') : p.appearance;
  const el = document.documentElement;
  el.dataset.theme = resolved;
  el.dataset.accent = p.accent;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#202124' : '#FFFFFF');
  return resolved;
}

export function ThemeProvider({ children, initial, onChange }: {
  children: ReactNode;
  /** Server-provided preferences (e.g. from the dz_theme claim). Wins over local cache. */
  initial?: Partial<ThemePrefs> | null;
  /** Called when the person changes a preference, so apps can persist it to their account. */
  onChange?: (p: ThemePrefs) => void;
}) {
  const [prefs, setPrefsState] = useState<ThemePrefs>(() => ({
    appearance: 'system', accent: 'cobalt', ...(readStored() ?? {}), ...(initial ?? {}),
  }));
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => (typeof document === 'undefined' ? 'light' : applyTheme(prefs)));

  useEffect(() => {
    if (!initial) return;
    setPrefsState((cur) => ({ ...cur, ...initial }));
  }, [initial?.appearance, initial?.accent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setResolved(applyTheme(prefs));
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
    if (prefs.appearance !== 'system') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const onSys = () => setResolved(applyTheme(prefs));
    mq.addEventListener('change', onSys);
    return () => mq.removeEventListener('change', onSys);
  }, [prefs]);

  const setPrefs = useCallback((p: Partial<ThemePrefs>) => {
    setPrefsState((cur) => {
      const next = { ...cur, ...p };
      onChange?.(next);
      return next;
    });
  }, [onChange]);

  const value = useMemo<ThemeContextValue>(() => ({
    ...prefs, resolved, setPrefs,
    setAppearance: (appearance) => setPrefs({ appearance }),
    setAccent: (accent) => setPrefs({ accent }),
  }), [prefs, resolved, setPrefs]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

/** Inline script for index.html <head>: sets the theme before first paint to avoid a flash. */
export const THEME_BOOT_SCRIPT = `try{var t=JSON.parse(localStorage.getItem('${STORAGE_KEY}')||'{}');var a=t.appearance||'system';var d=a==='dark'||(a==='system'&&matchMedia('(prefers-color-scheme: dark)').matches);document.documentElement.dataset.theme=d?'dark':'light';document.documentElement.dataset.accent=t.accent||'cobalt'}catch(e){}`;
